/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "../Stock/types";
import type { Supplier, SupplierItem } from "./types";
import { SupplierItemRow, type SupplierItemPatch } from "./SupplierItemRow";

type Props = { supplier: Supplier; onClose: () => void };

// What a supplier sells this shop, mapped to stock, with price history.
// Items are created automatically when a receipt from the supplier is posted.
export const SupplierItemsModal = defineComponent<Props>({
  name: "SupplierItemsModal",
  props: ["supplier", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const saving = ref(false);
    const items = ref<SupplierItem[]>([]);
    const stockItems = ref<StockItem[]>([]);

    const load = async () => {
      const [list, stock] = await Promise.all([
        trpc.purchasing.supplierItems.query({ supplier_id: props.supplier.id }),
        trpc.stock.list.query(),
      ]);
      items.value = list as SupplierItem[];
      stockItems.value = (stock as StockItem[]).filter((s) => s.active);
    };

    onMounted(async () => {
      try {
        await load();
      } catch {
        toast.showToast(t("inventory.suppliers.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    const save = async (patch: SupplierItemPatch) => {
      saving.value = true;
      try {
        await trpc.purchasing.updateSupplierItem.mutate(patch);
        await load();
        toast.showToast(t("inventory.suppliers.item_saved"), { type: "success" });
      } catch {
        toast.showToast(t("inventory.suppliers.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("inventory.suppliers.items_title").replace("{name}", props.supplier.name)}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="border-t p-4 text-right">
            <button class="btn btn-neutral" onClick={() => props.onClose()}>
              {t("inventory.purchases.close")}
            </button>
          </div>
        )}
      >
        <div class="p-6">
          {loading.value ? (
            <div class="flex justify-center p-6">
              <Loading />
            </div>
          ) : items.value.length === 0 ? (
            <p class="text-sm text-base-content/60">{t("inventory.suppliers.no_items")}</p>
          ) : (
            <div class="overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{t("inventory.suppliers.col_item")}</th>
                    <th>{t("inventory.suppliers.col_stock_item")}</th>
                    <th>{t("inventory.recipe.col_unit")}</th>
                    <th class="text-right">{t("inventory.suppliers.col_last_price")}</th>
                    <th>{t("inventory.suppliers.col_trend")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.value.map((item) => (
                    <SupplierItemRow
                      key={item.id}
                      item={item}
                      stockItems={stockItems.value}
                      saving={saving.value}
                      onSave={save}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </LemmonModal>
    );
  },
});
